import { createContext, type ReactNode, useCallback, useContext, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import type { UserEventOf } from "@/api";
import { NotificationsPanel } from "./NotificationsPanel";
import { notificationHref } from "./notificationHref";
import { UserEventsProvider } from "./UserEventsProvider";

type Toggle = (anchor: HTMLElement | null) => void;

interface PanelControl {
  toggle: Toggle;
  isOpen: boolean;
}

const PanelContext = createContext<PanelControl | null>(null);

/** A browser notification for events the user opted into (Settings > Notifications). */
function useDesktopNotification() {
  const navigate = useNavigate();
  const { t } = useTranslation("notifications");
  return useCallback(
    ({ data }: UserEventOf<"notification">) => {
      if (!data.showDesktop || typeof Notification === "undefined" || Notification.permission !== "granted") return;
      const popup = new Notification(data.title || t("desktopFallbackTitle"), {
        body: data.message,
        icon: "/favicon.svg",
        tag: data.uuid,
      });
      popup.onclick = () => {
        globalThis.focus();
        const href = notificationHref(data);
        if (href) navigate(href);
        popup.close();
      };
    },
    [navigate, t],
  );
}

/**
 * Notifications for the signed-in shell: the live user event stream, desktop
 * pop-ups, and the dropdown (opened from the sidebar bell via
 * useNotificationsPanel).
 */
export function NotificationsProvider({ children }: Readonly<{ children: ReactNode }>) {
  const [open, setOpen] = useState<{ anchor: HTMLElement | null } | null>(null);
  const close = useCallback(() => setOpen(null), []);
  const toggle = useCallback<Toggle>((anchor) => setOpen((current) => (current ? null : { anchor })), []);
  const onNotification = useDesktopNotification();
  const control = useMemo(() => ({ toggle, isOpen: open !== null }), [toggle, open]);

  return (
    <UserEventsProvider onNotification={onNotification}>
      <PanelContext.Provider value={control}>
        {children}
        {open && <NotificationsPanel anchor={open.anchor} onClose={close} />}
      </PanelContext.Provider>
    </UserEventsProvider>
  );
}

const CLOSED: PanelControl = { toggle: () => {}, isOpen: false };

/** Open/close the dropdown, anchored to `anchor` (null: the mobile sheet), and whether it's open. */
export function useNotificationsPanel(): PanelControl {
  return useContext(PanelContext) ?? CLOSED;
}
